/**
 * Studio AI assistant — field-context wiring.
 *
 * Two events flow into ReaderPage:
 *   `studio-assistant:field-context` — Aida should know the operator
 *      is working on the named field. Two sources:
 *        source: 'click' — fired by FieldHelpIcon. Forces the chat
 *          panel open + sets the active field. Clicking the same
 *          field's icon again closes the panel.
 *        source: 'blur'  — fired by a field's @blur handler. Only
 *          updates the active field if the chat panel is already open
 *          (no force-open on blur).
 *
 * Payload:
 *   { field, label, panel, value, source }
 *
 * Shared reactive state — ReaderPage writes here as the source of
 * truth; FieldHelpIcon reads to render its glyph (`?` when this
 * icon's field is NOT the active one, `×` when it IS active so the
 * operator sees a clear close affordance).
 */
import { reactive } from 'vue'

export const studioAssistantState = reactive({
  open: false,
  activeField: null,  // { field, label, panel, value } | null
})

export function dispatchFieldContext({ field, label, panel, value, source, meta }) {
  window.dispatchEvent(new CustomEvent('studio-assistant:field-context', {
    detail: { field, label, panel, value: value ?? '', source, meta: meta || null },
  }))
}

/**
 * Decide where a free-form studio-assistant chat message should route.
 *
 * Pure function so the routing rules are unit-testable independently of
 * ReaderPage's reactive state (ReaderPage.vue has no component tests).
 *
 * The invariant this enforces: a per-field help context only owns the
 * chat when it belongs to the surface the operator is actually on. While
 * in the Custom Deck brief chat, ONLY a custom-deck-scoped field (the
 * deck's own ? / blur icons) may intercept; a stale context from another
 * panel — most dangerously the reader `name` field, which spends
 * emeralds via the name-claim flow — must NOT hijack the deck chat.
 *
 * @param {{field: string, panel?: string}|null} assistantField active per-field help context
 * @param {boolean} customDeckChatActive whether the operator is in the Custom Deck brief chat
 * @returns {'name-claim'|'field-polish'|'custom-deck'|'nav'}
 */
export function decideStudioCreatorRoute(assistantField, customDeckChatActive) {
  // 'custom_deck_brief' is the WHOLE-FORM helper pill ("Help me fill this
  // out") — not a single polishable field. Let it fall through to the
  // brand-extract (custom-deck) chat that fills every deck field, instead
  // of per-field polish.
  if (assistantField && assistantField.field
      && assistantField.field !== 'custom_deck_brief') {
    const fieldOwnsChat =
      !customDeckChatActive || assistantField.panel === 'custom-deck'
    if (fieldOwnsChat) {
      return assistantField.field === 'name' ? 'name-claim' : 'field-polish'
    }
  }
  if (customDeckChatActive) return 'custom-deck'
  return 'nav'
}
